import React, { useRef, useState, useEffect, useId } from 'react';
import { createPortal } from 'react-dom';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { DotsThreeVerticalIcon, PlusIcon, NoteIcon } from '@phosphor-icons/react';
import type { ScriptCaretRequest } from './scriptEditing';
import { SlashMenu, BlockType } from './SlashMenu';
import { BlockOptionsMenu } from './BlockOptionsMenu';
import {completionContext,completionMatches,completionSuffix,cyclesCompletion,completedField,sceneTabText,type ScriptCompletionLists,type CompletionContext,type CompletionStage} from '../../lib/scriptCompletion';
import { AutocompleteMenu, AutocompleteItem } from './AutocompleteMenu';
import { RichScriptText, type RichScriptTextHandle } from './RichScriptText';
import {observedTextEdit,type ScriptRichText,type TextStyle,type ScriptTextEdit} from '../../lib/scriptRichText';
import type { ScriptDualDialogue } from '../../lib/scriptDualDialogue';
import { DualDialogueEditor } from './DualDialogueEditor';
import type {ScriptAlternates} from '../../lib/scriptAlternates';
import {DialogueAlternates} from './DialogueAlternates';
import type {ScriptRevisions,RevisionRangeMap} from '../../lib/scriptRevisions';
import {SCRIPT_BODY,SCRIPT_PAGE,scriptElementGeometry} from '../../lib/scriptLayout';
import {EMPTY_SCRIPT_FORMAT,resolveParagraphFormat,type ScriptFormatSource} from '../../lib/scriptFormat';
import {useDraftBlock} from './ScriptDraftContext';

export interface ScriptBlockData {
    id: string;
    type: BlockType;
    content: string;
    characterId?: string;
    richText?: ScriptRichText;
    dualDialogue?: ScriptDualDialogue;
    alternates?: ScriptAlternates;
}

interface ScriptBlockProps {
    format?:ScriptFormatSource;
    toolbarTarget?:HTMLElement|null;
    caretRequest?:ScriptCaretRequest;
    block: ScriptBlockData;
    isActive: boolean;
    onUpdate: (id: string, content: string, characterId?: string,edit?:ScriptTextEdit) => void;
    onFormat?: (id: string, start: number, end: number, style: TextStyle) => void;
    onParagraphAttributes?: (id: string, values: Record<string, string>) => void;
    onDualChange?: (id:string, value:ScriptDualDialogue, paragraphId?:string, command?:boolean) => void;
    onAlternateChange?: (paragraph:ScriptBlockData) => void;
    revisions?:ScriptRevisions;
    revisionRanges?:RevisionRangeMap;
    onRevisionMark?: (id:string,start:number,end:number,clear:boolean)=>void;
    onTypeChange: (id: string, type: BlockType) => void;
    onEnter: (id: string, splitContent?: string, currentContent?: string) => void;
    onDelete: (id: string) => void;
    onBackspace: (id: string) => void;
    onDuplicate?: (id: string) => void;
    onTab: (id: string) => void;
    onFocus: (id: string) => void;
    onMoveUp: (id: string) => void;
    onMoveDown: (id: string) => void;
    suggestions?: ScriptCompletionLists;
    isOverlay?: boolean;
    isSelected?: boolean;
    isMultiSelectMode?: boolean;
    onToggleSelection?: (id: string) => void;
    onEnterMultiSelectMode?: () => void;
    sceneNumber?: number | string;
    showSceneNumbers?: boolean;
    note?: string;
    onNoteChange?: (id: string, note: string) => void;
    readOnly?: boolean;
}

export const ScriptBlock: React.FC<ScriptBlockProps> = ({
    format=EMPTY_SCRIPT_FORMAT,
    block,
    toolbarTarget,
    caretRequest,
    isActive,
    onUpdate,
    onTypeChange,
    onFormat,
    onParagraphAttributes,
    onDualChange,
    onAlternateChange,
    revisions,
    revisionRanges,
    onRevisionMark,
    onEnter,
    onDelete,
    onBackspace,
    onDuplicate,
    onTab,
    onFocus,
    onMoveUp,
    onMoveDown,
    suggestions,
    isOverlay,
    isSelected,
    isMultiSelectMode,
    onToggleSelection,
    onEnterMultiSelectMode,
    sceneNumber,
    showSceneNumbers,
    note,
    onNoteChange,
    readOnly = false
}) => {
    const paragraphGeometry=resolveParagraphFormat(block,format,scriptElementGeometry(block.type));
    const projected=useDraftBlock(block.id),draft=isOverlay?undefined:projected;
    const textareaRef = useRef<RichScriptTextHandle>(null);
    const [showSlashMenu, setShowSlashMenu] = useState(false);
    const [slashMenuPos, setSlashMenuPos] = useState({ top: 0, left: 0 });

    // Block Menu State
    const [showBlockMenu, setShowBlockMenu] = useState(false);
    const [blockMenuPos, setBlockMenuPos] = useState({ top: 0, left: 0 });

    // Autocomplete Menu State
    const [showAutocomplete, setShowAutocomplete] = useState(false);
    const [autocompleteItems, setAutocompleteItems] = useState<AutocompleteItem[]>([]);
    const completionId=useId();
    const [completionIndex,setCompletionIndex]=useState(0);
    const [completionField,setCompletionField]=useState<CompletionContext>();
    const dismissedCompletion=useRef<CompletionStage|undefined>(undefined);
    const [autocompletePos, setAutocompletePos] = useState({ top: 0, left: 0 });

    // Note State
    const [showNoteEditor, setShowNoteEditor] = useState(false);
    const [localNote, setLocalNote] = useState(note || '');

    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging
    } = useSortable({ id: block.id, disabled: isOverlay || readOnly });

    useEffect(() => {
        if (isActive && textareaRef.current) {
            textareaRef.current.focus();
            if (caretRequest) textareaRef.current.setSelectionRange(caretRequest.start, caretRequest.end);
        }
    }, [isActive, caretRequest]);

    const formatSelection = (style: TextStyle) => {
        const editor = textareaRef.current;
        if (!editor || readOnly) return;
        const start = editor.selectionStart, end = editor.selectionEnd;
        onFormat?.(block.id, start, end, style);
        requestAnimationFrame(() => { editor.focus(); editor.setSelectionRange(start, end); });
    };
    const [showSceneIdentity, setShowSceneIdentity] = useState(false);
    const [sceneIdentity, setSceneIdentity] = useState<Record<string, string>>({});
    useEffect(() => { setShowSceneIdentity(false); }, [JSON.stringify(block.richText?.paragraphAttributes)]);

    const openElementMenu = () => {
        const rect = textareaRef.current?.getBoundingClientRect();
        if (!rect) return;
        setShowSlashMenu(true);
        setSlashMenuPos({top:Math.max(8,Math.min(rect.bottom + 8,window.innerHeight - 380)),
            left:Math.max(8,Math.min(rect.left,window.innerWidth - 296))});
    };
    const showCompletions = (text:string,all=false) => {
        const field=suggestions&&completionContext(block.type,text,suggestions);
        const matches=field&&suggestions?completionMatches(field,suggestions,all):[];
        if (!field || !matches.length || !all && dismissedCompletion.current===field.stage) {setShowAutocomplete(false);return;}
        const rect=textareaRef.current?.getBoundingClientRect();
        if(!rect)return;
        setAutocompletePos({top:Math.max(8,Math.min(rect.bottom+5,window.innerHeight-272)),left:Math.max(8,Math.min(rect.left,window.innerWidth-264))});
        setCompletionField(field);setAutocompleteItems(matches);setCompletionIndex(0);setShowAutocomplete(true);
    };
    const acceptCompletion = (item:AutocompleteItem,key:'Enter'|'Tab'|'click'|'ArrowRight') => {
        if(!suggestions||!completionField||readOnly)return;
        const advance=key==='Tab'||key==='click';
        const value=completedField(block.content,completionField,item,suggestions,advance);
        onUpdate(block.id,value,item.id,observedTextEdit(block.content,value,completionField.start,completionField.end));
        setShowAutocomplete(false);dismissedCompletion.current=undefined;
        const cue=completionField.stage==='characters'||completionField.stage==='extensions';
        const leavesParagraph=((cue||completionField.stage==='transitions')&&(key==='Enter'||key==='Tab'))||(completionField.stage==='timesOfDay'&&key==='Tab');
        if(!leavesParagraph)requestAnimationFrame(()=>{textareaRef.current?.focus();textareaRef.current?.setSelectionRange(value.length,value.length);});
        if((cue||completionField.stage==='transitions')&&key==='Enter')onEnter(block.id,'',value);
        else if((cue||completionField.stage==='timesOfDay'||completionField.stage==='transitions')&&key==='Tab')onTab(block.id);
        else if(key!=='ArrowRight'&&(completionField.stage==='sceneIntros'||(completionField.stage==='locations'&&advance)))showCompletions(value,true);
    };
    useEffect(()=>{if(!isActive)setShowAutocomplete(false);},[isActive]);
    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (readOnly || e.nativeEvent.isComposing || e.defaultPrevented || showSlashMenu) return;
        const completionAtCaret=textareaRef.current?.selectionStart===textareaRef.current?.selectionEnd
            && textareaRef.current?.selectionEnd===block.content.length;
        if(showAutocomplete&&!completionAtCaret)setShowAutocomplete(false);
        if(showAutocomplete&&completionAtCaret&&autocompleteItems.length&&!e.ctrlKey&&!e.metaKey&&!e.altKey
            &&cyclesCompletion(completionField,e.key)){
            e.preventDefault();
            setCompletionIndex(index=>(index+1)%autocompleteItems.length);
            return;
        }
        if (showAutocomplete && completionAtCaret && !e.ctrlKey && !e.metaKey && !e.altKey && !e.shiftKey && ['Enter','Tab','ArrowRight','ArrowUp','ArrowDown','Escape'].includes(e.key)) {
            e.preventDefault();
            if(e.key==='ArrowDown')setCompletionIndex(index=>Math.min(index+1,autocompleteItems.length-1));
            else if(e.key==='ArrowUp')setCompletionIndex(index=>Math.max(index-1,0));
            else if(e.key==='Escape'){dismissedCompletion.current=completionField?.stage;setShowAutocomplete(false);}
            else if(autocompleteItems[completionIndex])acceptCompletion(autocompleteItems[completionIndex],e.key as 'Enter'|'Tab'|'ArrowRight');
            return;
        }
        if(['ArrowLeft','ArrowRight','Home','End','Tab'].includes(e.key))setShowAutocomplete(false);
        if (e.altKey || (e.ctrlKey || e.metaKey) && ['Tab','Enter','ArrowUp','ArrowDown'].includes(e.key)) return;
        // Text formatting shortcuts
        if ((e.ctrlKey || e.metaKey) && !e.shiftKey) {
            if (e.key === 'b') {
                e.preventDefault();
                formatSelection('Bold');
                return;
            } else if (e.key === 'i') {
                e.preventDefault();
                formatSelection('Italic');
                return;
            } else if (e.key === 'u') {
                e.preventDefault();
                formatSelection('Underline');
                return;
            }
        }

        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            if (!block.content.trim()) {openElementMenu(); return;}
            const cursor = textareaRef.current?.selectionStart ?? 0;
            const selectionEnd = textareaRef.current?.selectionEnd ?? cursor;
            const contentBefore = block.content.slice(0, cursor);
            const contentAfter = block.content.slice(selectionEnd);
            onEnter(block.id, contentAfter, contentBefore);
        } else if (e.key === 'Backspace') {
            if (block.content === '') {
                e.preventDefault();
                onDelete(block.id);
            } else if (textareaRef.current?.selectionStart === 0 && textareaRef.current?.selectionEnd === 0) {
                e.preventDefault();
                onBackspace(block.id);
            }
        } else if (e.key === 'ArrowUp') {
            if (textareaRef.current?.selectionStart === 0 && textareaRef.current?.selectionEnd === 0) {
                e.preventDefault();
                onMoveUp(block.id);
            }
        } else if (e.key === 'ArrowDown') {
            if (textareaRef.current && textareaRef.current.selectionStart === block.content.length && textareaRef.current.selectionEnd === block.content.length) {
                e.preventDefault();
                onMoveDown(block.id);
            }
        } else if (e.key === '/' && block.content === '') {
            e.preventDefault();
            openElementMenu();
        } else if (e.key === 'Escape') {
            setShowSlashMenu(false);
        } else if (e.key === 'Tab' && !e.shiftKey) {
            e.preventDefault();
            const editor = textareaRef.current;
            if (editor && editor.selectionStart === editor.selectionEnd
                && (editor.selectionEnd === block.content.length || !block.content.trim()
                    || block.type === 'parenthetical' && block.content.trim() === '()')) {
                if(block.type==='scene-heading'&&suggestions&&block.content.trim()){
                    const field=completionContext(block.type,block.content,suggestions)!;
                    const value=sceneTabText(block.content,suggestions);
                    if(value!==undefined){
                        onUpdate(block.id,value,undefined,observedTextEdit(block.content,value,field.stage==='sceneIntros'?0:block.content.length,block.content.length));
                        dismissedCompletion.current=undefined;
                        requestAnimationFrame(()=>textareaRef.current?.setSelectionRange(value.length,value.length));
                        showCompletions(value,true);return;
                    }
                    if(field.stage!=='timesOfDay'||!field.query.trim()){dismissedCompletion.current=undefined;showCompletions(block.content,true);return;}
                }
                onTab(block.id);
            }
        }
    };

    const handleChange = (value: string,edit?:ScriptTextEdit) => {
        if (readOnly) return;
        let val = value;

        // Strict Uppercase enforcement for standard screenplay elements
        if (['scene-heading', 'character', 'transition', 'shot'].includes(block.type)) {
            val = val.toUpperCase();
        }

        onUpdate(block.id,val,undefined,edit?observedTextEdit(edit.before,val,edit.start,edit.end):undefined);

        if (showSlashMenu) {
            setShowSlashMenu(false);
        }

        if(!val.trim())dismissedCompletion.current=undefined;
        const editor=textareaRef.current;
        if(editor?.selectionStart===editor?.selectionEnd&&editor?.selectionEnd===value.length)showCompletions(val);
        else setShowAutocomplete(false);
    };

    const getStyles = () => {
        // Geometry comes from scriptElementGeometry; rich runs own overrides.
        const base = 'font-screenplay text-[12pt] leading-none';

        switch (block.type) {
            case 'scene-heading':
                return `${base} text-zinc-100 uppercase mb-0`;
            case 'character':
                return `${base} text-zinc-100 uppercase mb-0`;
            case 'dialogue':
                return `${base} text-zinc-100 mb-0`;
            case 'parenthetical':
                return `${base} text-zinc-100 mb-0`;
            case 'transition':
                return `${base} uppercase text-zinc-100 mb-0`;
            case 'shot':
                return `${base} uppercase text-zinc-100 w-full mb-0`;
            case 'dual-dialogue':
                return `${base} flex w-full gap-8 mb-0`;
            default: // action
                return `${base} text-zinc-100 w-full mb-0`;
        }
    };

    const handleGripClick = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (readOnly) return;
        const rect = e.currentTarget.getBoundingClientRect();
        setBlockMenuPos({
            top: rect.bottom + window.scrollY + 5,
            left: rect.left + window.scrollX
        });
        setShowBlockMenu(true);
    };

    if (isDragging && !isOverlay) {
        return (
            <div
                ref={setNodeRef}
                style={{
                    transform: CSS.Transform.toString(transform),
                    transition: transition || 'transform 200ms ease',
                }}
                className="relative w-full h-8 my-1 bg-blue-500/10 rounded"
            />
        );
    }

    if (isOverlay) {
        return (
            <div className={`relative flex items-center gap-2 px-4 py-2 bg-graphite border border-white/10 rounded shadow-2xl w-[600px]`}>
                <DotsThreeVerticalIcon className="w-4 h-4 text-zinc-500" />
                <div className={`whitespace-pre-wrap flex-1 ${getStyles()}`}>
                    {block.content || (block.type.replace('-', ' '))}
                </div>
            </div>
        );
    }

    return (
        <div
            ref={setNodeRef}
            style={{
                transform: CSS.Transform.toString(transform),
                transition,
                opacity: 1,
                marginTop:`${paragraphGeometry.before*SCRIPT_PAGE.lineHeight}pt`,
                ...(draft?{position:'absolute',top:`${draft.top}pt`,width:'100%',marginTop:0,minHeight:`${draft.height}pt`}:{}),
            }}
            className={`group relative ${isSelected ? 'bg-blue-500/10 ring-1 ring-blue-500/20 rounded-md -mx-2 px-2' : ''}`}
            data-block-id={block.id}
        >
            {/* The Click Target / Interaction Wrapper */}
            <div className={`relative flex items-start`}>

                {/* Note Indicator */}
                {note && (
                    <div
                        className="absolute -left-6 top-1 cursor-pointer"
                        onClick={() => { setLocalNote(note || ''); setShowNoteEditor(true); }}
                        title={note}
                    >
                        <div className="w-2 h-2 rounded-full bg-yellow-500/80 ring-2 ring-yellow-500/20" />
                    </div>
                )}

                {/* Notion-style Gutter Controls - Fixed position on left */}
                <div
                    data-story-gutter
                    className={`absolute -left-12 top-0 w-10 flex items-start justify-end gap-0.5 pr-2 transition-opacity select-none ${readOnly ? 'invisible pointer-events-none' : 'opacity-0 group-hover:opacity-100 pointer-events-none group-hover:pointer-events-auto'}`}
                >
                    {isMultiSelectMode ? (
                        <button
                            onClick={(e) => { e.stopPropagation(); onToggleSelection?.(block.id); }}
                            className={`p-1 rounded hover:bg-white/10 ${isSelected ? 'text-indigo-400 bg-indigo-500/10' : 'text-zinc-600 hover:text-zinc-200'}`}
                        >
                            {isSelected ? (
                                <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                            ) : (
                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <rect x="4" y="4" width="16" height="16" rx="2" strokeWidth="2" />
                                </svg>
                            )}
                        </button>
                    ) : (
                        <button
                            onClick={(e) => { e.stopPropagation(); onEnter(block.id); }}
                            className="p-1 rounded hover:bg-white/10 text-zinc-600 hover:text-zinc-200"
                        >
                            <PlusIcon className="w-3.5 h-3.5" />
                        </button>
                    )}
                    <button
                        onClick={(e) => { e.stopPropagation(); setLocalNote(note || ''); setShowNoteEditor(true); }}
                        className={`p-1 rounded hover:bg-white/10 ${note ? 'text-yellow-500' : 'text-zinc-600 hover:text-zinc-200'}`}
                        title={note ? 'Edit note' : 'Add note'}
                    >
                        <NoteIcon className="w-3.5 h-3.5" />
                    </button>
                    <div
                        {...attributes}
                        {...listeners}
                        onClick={handleGripClick}
                        className="grip-handle p-1 rounded cursor-grab active:cursor-grabbing hover:bg-white/10 text-zinc-600 hover:text-zinc-200"
                    >
                        <DotsThreeVerticalIcon className="w-3.5 h-3.5" />
                    </div>
                </div>

                <div className="flex-1 min-w-0 relative">
                    {isActive && !readOnly && toolbarTarget && block.type !== 'dual-dialogue' && createPortal(
                        <div className="flex items-center gap-1">
                            <span className="mr-2 text-zinc-500 capitalize">{block.type.replace('-', ' ')}</span>
                            {(['Bold', 'Italic', 'Underline'] as const).map(style => <button key={style}
                                aria-label={`${style} selected text`} title={`${style} (Ctrl/Cmd+${style[0]})`}
                                onMouseDown={event => event.preventDefault()} onClick={() => formatSelection(style)}><span className={style==='Bold'?'font-bold':style==='Italic'?'italic':'underline'}>{style[0]}</span></button>)}
                            {revisions&&[false,true].map(clear=><button key={String(clear)} className="px-2 py-1 rounded hover:bg-white/10" onMouseDown={e=>e.preventDefault()}
                                onClick={()=>{const editor=textareaRef.current;if(editor){const start=editor.selectionStart,end=editor.selectionEnd;onRevisionMark?.(block.id,start,end,clear);requestAnimationFrame(()=>{editor.focus();editor.setSelectionRange(start,end);});}}}>{clear?'Clear revision':'Mark revision'}</button>)}
                            {block.type === 'scene-heading' && <button className="px-2 py-1 rounded hover:bg-white/10"
                                onClick={() => {setSceneIdentity({...block.richText?.paragraphAttributes}); setShowSceneIdentity(true);}}>Scene identity</button>}
                            <DialogueAlternates compact paragraph={block} readOnly={readOnly} onChange={paragraph=>onAlternateChange?.(paragraph)} />
                        </div>,toolbarTarget
                    )}
                    {showSceneIdentity && !readOnly && <div data-story-native-undo="true" className="mb-3 p-3 bg-zinc-900 rounded font-sans text-xs" style={draft?{position:'absolute',top:'20pt',left:0,zIndex:40}:undefined}>
                        {['Number', 'Bookmark', 'Label'].map(key => <label key={key} className="block mb-2 text-zinc-400">{key}
                            <input aria-label={`Scene ${key.toLowerCase()}`} value={sceneIdentity[key] ?? ''}
                                onChange={event => setSceneIdentity(previous => ({...previous, [key]: event.target.value}))}
                                className="block w-full bg-zinc-800 p-1 text-white" /></label>)}
                        <button className="mr-4 text-indigo-300" onClick={() => {onParagraphAttributes?.(block.id, sceneIdentity); setShowSceneIdentity(false);}}>Save scene identity</button>
                        <button onClick={() => setShowSceneIdentity(false)}>Cancel</button>
                    </div>}
                    {block.type === 'dual-dialogue' ? <DualDialogueEditor block={block} readOnly={readOnly} isActive={isActive} toolbarTarget={toolbarTarget} draft={draft}
                        revisions={revisions}
                        revisionRanges={revisionRanges}
                        onFocus={() => onFocus(block.id)} onChange={(data,paragraphId,command) => onDualChange?.(block.id,data,paragraphId,command)} /> : (
                        <>
                            <div className="relative">
                                {block.type === 'scene-heading' && showSceneNumbers && sceneNumber && <span
                                    className="absolute -left-16 top-0 w-10 text-right text-[10px] font-mono text-zinc-500">{sceneNumber}</span>}
                            <RichScriptText
                                lines={draft?.fields.get('content')}
                                originX={paragraphGeometry.x}
                                style={{marginLeft:`${paragraphGeometry.x-SCRIPT_BODY.left}pt`,width:`${paragraphGeometry.width}pt`,textAlign:paragraphGeometry.align,textIndent:`${paragraphGeometry.firstIndent}pt`}}
                                revisions={revisions}
                                revisionRanges={revisionRanges?.content}
                                ref={textareaRef}
                                block={block}
                                onChange={handleChange}
                                onKeyDown={handleKeyDown}
                                onFocus={() => onFocus(block.id)}
                                onBlur={()=>setShowAutocomplete(false)}
                                completion={showAutocomplete?{id:completionId,index:completionIndex}:undefined}
                                suggestedSuffix={showAutocomplete?completionSuffix(completionField,autocompleteItems[completionIndex]):undefined}
                                readOnly={readOnly}
                                placeholder={isActive && block.content === '' && block.type === 'action' ? `Type '/' for commands` : ''}
                                className={`
                                    bg-transparent resize-none outline-none placeholder:text-zinc-600/50 empty:before:content-[attr(data-placeholder)] empty:before:text-zinc-600/50
                                    transition-all duration-200 break-words p-0 border-none ring-0 w-full
                                    ${getStyles()}
                                    ${readOnly ? 'cursor-default' : ''}
                                `}
                            />

                            </div>
                            {/* Character Extensions Quick Actions */}
                            {!readOnly && toolbarTarget && block.type === 'character' && isActive && block.content.length > 0 && createPortal(
                                <div className="flex items-center gap-1">
                                    {['V.O.', 'O.S.', 'CONT\'D', 'O.C.'].map((ext) => {
                                        const hasExt = block.content.toUpperCase().includes(`(${ext})`);
                                        return (
                                            <button
                                                key={ext}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    let newContent = block.content.replace(/\s*\([^)]*\)\s*$/g, '').trim();
                                                    if (!hasExt) {
                                                        newContent = `${newContent} (${ext})`;
                                                    }
                                                    onUpdate(block.id, newContent.toUpperCase());
                                                }}
                                                className={`px-1.5 py-0.5 text-[9px] font-mono rounded transition-all ${hasExt
                                                    ? 'bg-indigo-500/30 text-indigo-300 border border-indigo-500/30'
                                                    : 'bg-zinc-800 text-zinc-500 hover:text-zinc-300 hover:bg-zinc-700 border border-transparent'
                                                    }`}
                                            >
                                                {ext}
                                            </button>
                                        );
                                    })}
                                </div>, toolbarTarget
                            )}
                        </>
                    )}
                </div>
            </div>

            {!readOnly && showSlashMenu && createPortal(
                <SlashMenu
                    position={slashMenuPos}
                    onSelect={(type) => {
                        if (readOnly) return;
                        onTypeChange(block.id, type);
                        setShowSlashMenu(false);
                        requestAnimationFrame(() => {textareaRef.current?.focus(); const start = type === 'parenthetical' ? 1 : 0; textareaRef.current?.setSelectionRange(start,start);});
                    }}
                    onClose={() => {setShowSlashMenu(false); textareaRef.current?.focus();}}
                />,
                document.body
            )}

            {!readOnly && showBlockMenu && createPortal(
                <BlockOptionsMenu
                    position={blockMenuPos}
                    currentType={block.type}
                    onClose={() => setShowBlockMenu(false)}
                    onDelete={() => onDelete(block.id)}
                    onDuplicate={() => onDuplicate ? onDuplicate(block.id) : onEnter(block.id)}
                    onTypeChange={(type) => onTypeChange(block.id, type)}
                    onMultiSelect={onEnterMultiSelectMode}
                />,
                document.body
            )}

            {!readOnly && showAutocomplete && createPortal(
                <AutocompleteMenu
                    position={autocompletePos}
                    items={autocompleteItems}
                    selectedIndex={completionIndex} listId={completionId}
                    label={`${completionField?.stage==='timesOfDay'?'Time of day':completionField?.stage==='sceneIntros'?'Scene intro':completionField?.stage==='locations'?'Location':completionField?.stage==='extensions'?'Character extension':completionField?.stage==='transitions'?'Transition':'Character'} suggestions`}
                    onSelect={item=>acceptCompletion(item,'click')}
                    onClose={() => setShowAutocomplete(false)}
                />,
                document.body
            )}

            {/* Note Editor Popup */}
            {!readOnly && showNoteEditor && createPortal(
                <div
                    className="fixed top-0 bottom-0 z-[9999] flex items-center justify-center p-4 bg-black/50"
                    style={{ left: 'var(--sidebar-left, 0px)', right: 'var(--sidebar-right, 0px)' }}
                    onClick={() => {
                        if (onNoteChange) onNoteChange(block.id, localNote);
                        setShowNoteEditor(false);
                    }}
                >
                    <div
                        className="bg-zinc-900 border border-white/10 rounded-lg shadow-2xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in-95"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="p-3 border-b border-white/5 flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <div className="w-2 h-2 rounded-full bg-yellow-500" />
                                <span className="text-sm font-medium text-zinc-200">Block Note</span>
                            </div>
                            <button
                                onClick={() => {
                                    setLocalNote('');
                                    if (onNoteChange) onNoteChange(block.id, '');
                                    setShowNoteEditor(false);
                                }}
                                className="text-[10px] text-red-400 hover:text-red-300"
                            >
                                Remove Note
                            </button>
                        </div>
                        <div className="p-3">
                            <textarea
                                value={localNote}
                                onChange={(e) => setLocalNote(e.target.value)}
                                placeholder="Add a note for this block..."
                                className="w-full h-24 bg-white/5 border border-white/10 rounded-lg p-3 text-sm text-zinc-200 placeholder:text-zinc-600 outline-none resize-none focus:border-yellow-500/50"
                                autoFocus
                            />
                        </div>
                        <div className="p-3 border-t border-white/5 flex justify-end gap-2">
                            <button
                                onClick={() => setShowNoteEditor(false)}
                                className="px-3 py-1.5 text-xs text-zinc-400 hover:text-white"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={() => {
                                    if (onNoteChange) onNoteChange(block.id, localNote);
                                    setShowNoteEditor(false);
                                }}
                                className="px-3 py-1.5 text-xs bg-yellow-600 hover:bg-yellow-500 text-white rounded"
                            >
                                Save Note
                            </button>
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
};
